import { useState, useCallback, useEffect, useRef } from 'react'
import { TIMEOUTS } from '../constants'

interface UseApiOptions {
  // Skip initial fetch on mount
  skip?: boolean
  // Credentials mode
  credentials?: RequestCredentials
  // Retry count for 401 errors (auth race condition)
  retryOn401?: number
}

interface UseApiResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Generic hook for fetching data from API endpoints.
 * Handles loading state, error handling, and retries for auth race conditions.
 */
export function useApi<T>(
  url: string,
  options: UseApiOptions = {}
): UseApiResult<T> {
  const { skip = false, credentials = 'same-origin', retryOn401 = 2 } = options

  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(!skip)
  const [error, setError] = useState<string | null>(null)
  const retryCountRef = useRef(0)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    retryCountRef.current = 0

    const doFetch = async (): Promise<void> => {
      try {
        const response = await fetch(url, {
          credentials,
          cache: 'no-store',
        })

        if (!response.ok) {
          // Handle auth race condition on slow networks
          if (response.status === 401 && retryCountRef.current < retryOn401) {
            retryCountRef.current++
            await new Promise(resolve => setTimeout(resolve, TIMEOUTS.AUTH_RETRY_DELAY))
            return doFetch()
          }

          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || errorData.message || `Request failed: ${response.status}`)
        }

        const result = await response.json()
        setData(result)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        setData(null)
      } finally {
        setLoading(false)
      }
    }

    await doFetch()
  }, [url, credentials, retryOn401])

  useEffect(() => {
    if (!skip) {
      fetchData()
    }
  }, [skip, fetchData])

  return { data, loading, error, refetch: fetchData }
}

interface UseMutationOptions {
  credentials?: RequestCredentials
}

interface UseMutationResult<TData, TVariables> {
  mutate: (variables: TVariables) => Promise<TData>
  data: TData | null
  loading: boolean
  error: string | null
  reset: () => void
}

/**
 * Hook for POST/PUT/DELETE operations.
 * Returns a mutate function that can be called to perform the mutation.
 */
export function useMutation<TData = unknown, TVariables = unknown>(
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  options: UseMutationOptions = {}
): UseMutationResult<TData, TVariables> {
  const { credentials = 'same-origin' } = options

  const [data, setData] = useState<TData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutate = useCallback(async (variables: TVariables): Promise<TData> => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(url, {
        method,
        credentials,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variables),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || result.message || `Request failed: ${response.status}`)
      }

      setData(result)
      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [url, method, credentials])

  const reset = useCallback(() => {
    setData(null)
    setError(null)
    setLoading(false)
  }, [])

  return { mutate, data, loading, error, reset }
}
